import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import type { ConfluenceUpdatePageCommandProperties } from '@atlassian/rovo-content-bridge-api-commands/commands/confluence-update-page-command';

import type { DocumentDynamicUiUpdateCommandProcessor } from '../../stream-processors/update-dynamic-ui-command-processor';
import type { DYNAMIC_UI_CREATE_KEY } from '../../types/command-manager';
import type { CwrRemixVisualMetadata } from '../../types/cwr-remix-result';
import type { PageUpdateStrategy } from '../page-update-strategy';

type DynamicUiUpdatePayload = Extract<
	ConfluenceUpdatePageCommandProperties,
	{ key: typeof DYNAMIC_UI_CREATE_KEY }
>;

type RecordCwrRemixVisualMetadata = (
	singleInstrumentationId: string,
	resultId: string,
	metadata: CwrRemixVisualMetadata,
) => void;

export class DynamicUiUpdateStrategy implements PageUpdateStrategy {
	constructor(
		private processor: DocumentDynamicUiUpdateCommandProcessor,
		private recordCwrRemixVisualMetadata?: RecordCwrRemixVisualMetadata | undefined,
	) {}

	async process(payload: ConfluenceUpdatePageCommandProperties): Promise<void> {
		const {
			data,
			isStreamComplete,
			traceId,
			singleInstrumentationId,
			isInlineEdit,
			chatRemixInstrumentationId,
			cwrRemixInvokedFrom,
		} = payload as DynamicUiUpdatePayload;
		if (
			cwrRemixInvokedFrom &&
			singleInstrumentationId &&
			data.contentType &&
			isExperimentEnabled('platform_editor_cwr_remix_acceptance')
		) {
			this.recordCwrRemixVisualMetadata?.(
				singleInstrumentationId,
				data.pageReadyMauiId ?? data.id,
				{
					dynamicUiType: data.contentType,
					...(data.dynamicUiSubtype && { dynamicUiSubtype: data.dynamicUiSubtype }),
				},
			);
		}
		await this.processor.streamChunk(
			data,
			isStreamComplete,
			traceId,
			singleInstrumentationId,
			isInlineEdit,
			chatRemixInstrumentationId,
			cwrRemixInvokedFrom,
		);
	}

	abort(): void {
		this.processor.abort();
	}

	finish(): Promise<undefined> {
		this.processor.finish();
		return Promise.resolve(undefined);
	}
}
